import React from 'react';

const COOKIES = [
  {
    name: '_ga_#',
    purpose:
      'Used to distinguish individual users by means of designation of a randomly generated number as client identifier, which allows calculation of visits and sessions',
    provider: '.starlightsaga.com',
    service: 'Google Analytics',
    serviceUrl: 'https://business.safety.google/privacy/',
    type: 'http_cookie',
    expires: '1 year 1 month 4 days',
  },
  {
    name: '_ga',
    purpose: 'Records a particular ID used to come up with data about website usage by the user',
    provider: '.starlightsaga.com',
    service: 'Google Analytics',
    serviceUrl: 'https://business.safety.google/privacy/',
    type: 'http_cookie',
    expires: '1 year 1 month 4 days',
  },
];

const BROWSER_LINKS = [
  { label: 'Chrome', href: 'https://support.google.com/chrome/answer/95647#zippy=%2Callow-or-block-cookies' },
  { label: 'Internet Explorer', href: 'https://support.microsoft.com/en-us/windows/delete-and-manage-cookies-168dab11-0753-043d-7c16-ede5947fc64d' },
  { label: 'Firefox', href: 'https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop?redirectslug=enable-and-disable-cookies-website-preferences&redirectlocale=en-US' },
  { label: 'Safari', href: 'https://support.apple.com/en-ie/guide/safari/sfri11471/mac' },
  { label: 'Edge', href: 'https://support.microsoft.com/en-us/windows/microsoft-edge-browsing-data-and-privacy-bb8174ba-9d73-dcf2-9b4a-c582b4e640dd' },
  { label: 'Opera', href: 'https://help.opera.com/en/latest/web-preferences/' },
];

const AD_LINKS = [
  { label: 'Digital Advertising Alliance', href: 'http://www.aboutads.info/choices/' },
  { label: 'Digital Advertising Alliance of Canada', href: 'https://youradchoices.ca/' },
  { label: 'European Interactive Digital Advertising Alliance', href: 'http://www.youronlinechoices.com/' },
];

const ExternalLink = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
);

const CookiePolicy = () => {
  return (
    <div className="privacy-container">
      <div className="privacy-content">
        <h1>COOKIE POLICY</h1>
        <p className="privacy-subtitle"><strong>Last updated October 22, 2026</strong></p>

        <p>
          This Cookie Policy explains how <strong>My Saga LLC</strong> ("<strong>Company</strong>," "<strong>we</strong>," "<strong>us</strong>," and "<strong>our</strong>") uses cookies and similar technologies to recognize you when you visit our website at <ExternalLink href="https://starlightsaga.com">https://starlightsaga.com</ExternalLink> ("<strong>Website</strong>"). It explains what these technologies are and why we use them, as well as your rights to control our use of them.
        </p>
        <p>
          In some cases we may use cookies to collect personal information, or that becomes personal information if we combine it with other information.
        </p>

        <h2>What are cookies?</h2>
        <p>
          Cookies are small data files that are placed on your computer or mobile device when you visit a website. Cookies are widely used by website owners in order to make their websites work, or to work more efficiently, as well as to provide reporting information.
        </p>
        <p>
          Cookies set by the website owner (in this case, My Saga LLC) are called "first-party cookies." Cookies set by parties other than the website owner are called "third-party cookies." Third-party cookies enable third-party features or functionality to be provided on or through the website (e.g., advertising, interactive content, and analytics). The parties that set these third-party cookies can recognize your computer both when it visits the website in question and also when it visits certain other websites.
        </p>

        <h2>Why do we use cookies?</h2>
        <p>
          We use first- and third-party cookies for several reasons. Some cookies are required for technical reasons in order for our Website to operate, and we refer to these as "essential" or "strictly necessary" cookies. Other cookies also enable us to track and target the interests of our users to enhance the experience on our Online Properties. Third parties serve cookies through our Website for advertising, analytics, and other purposes. This is described in more detail below.
        </p>

        <h2 id="control">How can I control cookies?</h2>
        <p>
          You have the right to decide whether to accept or reject cookies. You can exercise your cookie rights by setting your preferences in the Cookie Preference Center. The Cookie Preference Center allows you to select which categories of cookies you accept or reject. Essential cookies cannot be rejected as they are strictly necessary to provide you with services.
        </p>
        <p>
          The Cookie Preference Center can be found in the notification banner and on our Website. If you choose to reject cookies, you may still use our Website though your access to some functionality and areas of our Website may be restricted. You may also set or amend your web browser controls to accept or refuse cookies.
        </p>
        <p>
          The specific types of first- and third-party cookies served through our Website and the purposes they perform are described in the table below (please note that the specific cookies served may vary depending on the specific Online Properties you visit):
        </p>

        <h3><u>Analytics and customization cookies:</u></h3>
        <p>
          These cookies collect information that is used either in aggregate form to help us understand how our Website is being used or how effective our marketing campaigns are, or to help us customize our Website for you.
        </p>
        {COOKIES.map((cookie) => (
          <table className="privacy-table" key={cookie.name}>
            <tbody>
              <tr><th>Name:</th><td>{cookie.name}</td></tr>
              <tr><th>Purpose:</th><td>{cookie.purpose}</td></tr>
              <tr><th>Provider:</th><td>{cookie.provider}</td></tr>
              <tr>
                <th>Service:</th>
                <td>
                  {cookie.service}{' '}
                  <ExternalLink href={cookie.serviceUrl}>View Service Privacy Policy</ExternalLink>
                </td>
              </tr>
              <tr><th>Type:</th><td>{cookie.type}</td></tr>
              <tr><th>Expires in:</th><td>{cookie.expires}</td></tr>
            </tbody>
          </table>
        ))}

        <h2>Do my choices apply on all my devices?</h2>
        <p>
          No. We save your cookie choices in the browser you used to make them. They apply to that browser on that device only.
        </p>
        <p>
          We ask for your choices again when you visit from a different device or browser, when you use a private or incognito window since your browser deletes your choices when you close it, or when you clear your cookies or browser data.
        </p>
        <p>You can change your choices at any time in the Cookie Preference Center.</p>

        <h2>How can I control cookies on my browser?</h2>
        <p>
          As the means by which you can refuse cookies through your web browser controls vary from browser to browser, you should visit your browser's help menu for more information. The following is information about how to manage cookies on the most popular browsers:
        </p>
        <ul>
          {BROWSER_LINKS.map(({ label, href }) => (
            <li key={label}><ExternalLink href={href}>{label}</ExternalLink></li>
          ))}
        </ul>
        <p>
          In addition, most advertising networks offer you a way to opt out of targeted advertising. If you would like to find out more information, please visit:
        </p>
        <ul>
          {AD_LINKS.map(({ label, href }) => (
            <li key={label}><ExternalLink href={href}>{label}</ExternalLink></li>
          ))}
        </ul>

        <h2>What about other tracking technologies, like web beacons?</h2>
        <p>
          Cookies are not the only way to recognize or track visitors to a website. We may use other, similar technologies from time to time, like web beacons (sometimes called "tracking pixels" or "clear gifs"). These are tiny graphics files that contain a unique identifier that enables us to recognize when someone has visited our Website or opened an email including them. This allows us, for example, to monitor the traffic patterns of users from one page within a website to another, to deliver or communicate with cookies, to understand whether you have come to the website from an online advertisement displayed on a third-party website, to improve site performance, and to measure the success of email marketing campaigns. In many instances, these technologies are reliant on cookies to function properly, and so declining cookies will impair their functioning.
        </p>

        <h2>Do you serve targeted advertising?</h2>
        <p>
          Third parties may serve cookies on your computer or mobile device to serve advertising through our Website. These companies may use information about your visits to this and other websites in order to provide relevant advertisements about goods and services that you may be interested in. They may also employ technology that is used to measure the effectiveness of advertisements. They can accomplish this by using cookies or web beacons to collect information about your visits to this and other sites in order to provide relevant advertisements about goods and services of potential interest to you. The information collected through this process does not enable us or them to identify your name, contact details, or other details that directly identify you unless you choose to provide these.
        </p>

        <h2>How often will you update this Cookie Policy?</h2>
        <p>
          We may update this Cookie Policy from time to time in order to reflect, for example, changes to the cookies we use or for other operational, legal, or regulatory reasons. Please therefore revisit this Cookie Policy regularly to stay informed about our use of cookies and related technologies.
        </p>
        <p>The date at the top of this Cookie Policy indicates when it was last updated.</p>

        <h2>Where can I get further information?</h2>
        <p>If you have any questions about our use of cookies or other technologies, please contact us at:</p>
        <p>
          <strong>My Saga LLC</strong><br />
          502 W 7th St<br />
          Erie, PA 16502<br />
          United States<br />
          support@starlightsaga.com
        </p>
      </div>
    </div>
  );
};

export default CookiePolicy;
